import { useEffect, useState } from 'react'
import { fetchPartsPage } from '../services/inventoryApi'
import { partTitle } from '../utils/stock'

const MATCH_LABELS = {
  MATCHED: '연결됨',
  MATCH_REQUIRED: '확인 필요',
  NEW_ITEM: '미등록',
}

function nonNegativeInt(value) {
  if (value === '' || value == null) return 0
  const number = Number.parseInt(value, 10)
  return Number.isInteger(number) && number >= 0 ? number : 0
}

export const QUOTE_REVIEW_FILTERS = [
  { id: 'all', label: '전체' },
  { id: 'new', label: '미등록' },
  { id: 'matched', label: '연결됨' },
  { id: 'review', label: '확인 필요' },
  { id: 'sufficient', label: '재고 충분' },
  { id: 'included', label: '요청 포함' },
]

export function matchesQuoteFilter(item, filter) {
  if (filter === 'new') return item.matchStatus === 'NEW_ITEM'
  if (filter === 'matched') return item.matchStatus === 'MATCHED'
  if (filter === 'review') return item.matchStatus === 'MATCH_REQUIRED' || Boolean(item.warnings?.length)
  if (filter === 'sufficient') return Boolean(item.matchedPart) && item.shortageQuantity === 0
  if (filter === 'included') return item.includeInRequest === true
  return true
}

function PartCandidateSearch({ onSelect }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    const q = query.trim()
    if (!q) {
      setResults([])
      return undefined
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      setSearching(true)
      try {
        const result = await fetchPartsPage({ q, field: 'model', page: 1, limit: 8 })
        if (!cancelled) setResults(result.items.filter((part) => !part.mergedIntoPartId))
      } catch (error) {
        if (!cancelled) setResults([])
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query])

  return (
    <div>
      <input
        className="input"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="모델명 검색"
        aria-label="ENSIGHT 품목 검색"
      />
      {searching ? <p className="muted">검색 중...</p> : null}
      {results.length > 0 ? (
        <ul className="merge-search-list">
          {results.map((part) => (
            <li key={part.id}>
              <button type="button" className="merge-search-item" onClick={() => onSelect(part)}>
                <strong className="model-name">{partTitle(part)}</strong>
                <span>
                  {part.partCode} · {part.manufacturer || '-'} · 재고 {part.quantity}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export default function QuoteImportReviewTable({ items, filter = 'all', onChange }) {
  const visibleRows = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => matchesQuoteFilter(item, filter))

  function update(index, patch) {
    onChange(items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)))
  }

  function applyQuantity(index, item, quantity) {
    const required = quantity === '' ? null : Number(quantity)
    const numeric = Number.isFinite(required) ? required : null
    if (item.matchedPart) {
      const shortage = numeric == null ? null : Math.max(numeric - Number(item.currentQuantity || 0), 0)
      update(index, {
        quantity: numeric,
        shortageQuantity: shortage,
        suggestedRequestQuantity: shortage,
        requestedQty: shortage > 0 ? Math.max(1, Math.round(shortage)) : 0,
        includeInRequest: shortage > 0,
      })
      return
    }
    update(index, {
      quantity: numeric,
      suggestedRequestQuantity: numeric,
      requestedQty: numeric > 0 ? Math.max(1, Math.round(numeric)) : 0,
      includeInRequest: numeric > 0,
    })
  }

  function selectPart(index, item, part) {
    const required = Number(item.quantity)
    const current = Number(part.quantity) || 0
    const shortage = Number.isFinite(required) ? Math.max(required - current, 0) : null
    update(index, {
      matchStatus: 'MATCHED',
      matchedPart: {
        id: part.id,
        partCode: part.partCode,
        manufacturer: part.manufacturer,
        model: part.model,
        name: part.name,
        quantity: current,
        unit: part.unit,
      },
      matchedPartId: part.id,
      currentQuantity: current,
      shortageQuantity: shortage,
      suggestedRequestQuantity: shortage,
      includeInRequest: shortage > 0,
      requestedQty: shortage > 0 ? Math.max(1, Math.round(shortage)) : 0,
    })
  }

  function markNew(index, item) {
    const required = Number(item.quantity)
    update(index, {
      matchStatus: 'NEW_ITEM',
      matchedPart: null,
      matchedPartId: '',
      currentQuantity: null,
      shortageQuantity: null,
      suggestedRequestQuantity: Number.isFinite(required) ? required : null,
      includeInRequest: required > 0,
      requestedQty: required > 0 ? Math.max(1, Math.round(required)) : 0,
      saveAlias: false,
    })
  }

  return (
    <div className="table-wrap quote-review-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <th>No</th>
            <th>공급처코드</th>
            <th>모델명</th>
            <th>품명</th>
            <th>필요수량</th>
            <th>현재재고</th>
            <th>부족수량</th>
            <th>요청수량</th>
            <th>매칭상태</th>
            <th>ENSIGHT 재고품목</th>
            <th>요청포함</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.length === 0 ? (
            <tr>
              <td colSpan={11} className="muted">이 필터에 해당하는 품목이 없습니다.</td>
            </tr>
          ) : null}
          {visibleRows.map(({ item, index }) => (
            <tr key={item.key || index}>
              <td>{item.rowNumber || index + 1}</td>
              <td>{item.vendorCode || '-'}</td>
              <td>
                <input
                  className="input"
                  value={item.model || ''}
                  onChange={(event) => update(index, { model: event.target.value })}
                  aria-label="모델명"
                />
              </td>
              <td>
                <input
                  className="input"
                  value={item.name || ''}
                  onChange={(event) => update(index, { name: event.target.value })}
                  aria-label="품명"
                />
              </td>
              <td>
                <input
                  className="input"
                  value={item.quantity ?? ''}
                  onChange={(event) => applyQuantity(index, item, event.target.value)}
                  inputMode="decimal"
                  aria-label="필요수량"
                />
              </td>
              <td>{item.currentQuantity == null ? '-' : item.currentQuantity}</td>
              <td>{item.shortageQuantity == null ? '-' : item.shortageQuantity}</td>
              <td>
                <input
                  className="input"
                  value={item.requestedQty}
                  onChange={(event) => update(index, { requestedQty: nonNegativeInt(event.target.value) })}
                  inputMode="numeric"
                  aria-label="요청수량"
                />
              </td>
              <td>
                <span className="chip chip--static quote-match-chip">{MATCH_LABELS[item.matchStatus] || item.matchStatus}</span>
                {item.matchedPart && item.shortageQuantity === 0 ? (
                  <span className="chip chip--static quote-match-chip">재고 충분</span>
                ) : null}
              </td>
              <td>
                {item.matchedPart ? (
                  <div>
                    <strong className="model-name">{item.matchedPart.model || item.matchedPart.name}</strong>
                    <span className="muted"> {item.matchedPart.partCode}</span>
                    <label className="field field--inline">
                      <input
                        type="checkbox"
                        checked={Boolean(item.saveAlias)}
                        onChange={(event) => update(index, { saveAlias: event.target.checked })}
                      />
                      <span>다음부터 자동 연결</span>
                    </label>
                  </div>
                ) : (
                  <PartCandidateSearch onSelect={(part) => selectPart(index, item, part)} />
                )}
                {item.matchStatus !== 'NEW_ITEM' ? (
                  <button type="button" className="btn btn--ghost btn--small" onClick={() => markNew(index, item)}>
                    미등록으로 요청
                  </button>
                ) : (
                  <span className="muted">재고에 없는 구매요청</span>
                )}
                {item.warnings?.length ? <p className="muted">{item.warnings[0]}</p> : null}
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={Boolean(item.includeInRequest)}
                  onChange={(event) => update(index, { includeInRequest: event.target.checked })}
                  aria-label="요청포함"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
